/**
 * PlanForm validation and payload helpers.
 *
 * Extracted from PlanForm.js to reduce file size.
 */

/**
 * Validate plan form data. Returns error string or null if valid.
 *
 * @param {Object} formData - Current form state
 * @param {boolean} isEditing - Whether editing an existing plan
 * @returns {string|null} Error message, or null if valid
 */
export function validatePlanForm(formData, isEditing) {
  // Validate ID format for new plans
  if (!isEditing && !/^[a-z0-9_-]+$/.test(formData.id)) {
    return "Plan ID must be lowercase alphanumeric with hyphens/underscores only";
  }

  if (!formData.name.trim()) {
    return "Plan name is required";
  }

  // [I2 FIX] Validate credit fields — empty string becomes Number("") = 0,
  // which silently blocks all usage for that model family.
  const creditFields = [
    { field: "monthlyHaikuCredits", label: "Haiku" },
    { field: "monthlySonnetCredits", label: "Sonnet" },
    { field: "monthlyOpusCredits", label: "Opus" },
  ];
  for (const { field, label } of creditFields) {
    const val = formData[field];
    // null = unlimited (checkbox checked) — skip validation
    if (val === null) continue;
    if (val === "" || val === undefined) {
      return `${label} credits cannot be blank. Enter a number, or check "Unlimited".`;
    }
    const num = Number(val);
    if (!Number.isInteger(num) || num < 0) {
      return `${label} credits must be a non-negative integer.`;
    }
  }

  return null;
}

/**
 * Build API payload from form data.
 *
 * @param {Object} formData - Current form state
 * @param {boolean} isEditing - Whether editing an existing plan
 * @returns {Object} Payload ready for API submission
 */
export function buildPlanPayload(formData, isEditing) {
  // Strip keys and filter empty features
  const features = formData.features.map((f) => f.value.trim()).filter(Boolean);

  const payload = {
    name: formData.name.trim(),
    price: formData.price === null ? null : Number(formData.price),
    sortOrder: Number(formData.sortOrder),
    stripePriceId: formData.stripePriceId.trim() || null,
    isPrivate: formData.isPrivate,
    limitWidgets:
      formData.limitWidgets === null ? null : Number(formData.limitWidgets),
    limitKnowledgeSources:
      formData.limitKnowledgeSources === null
        ? null
        : Number(formData.limitKnowledgeSources),
    // Credit allocations per model family (null = unlimited)
    monthlyHaikuCredits:
      formData.monthlyHaikuCredits === null
        ? null
        : Number(formData.monthlyHaikuCredits),
    monthlySonnetCredits:
      formData.monthlySonnetCredits === null
        ? null
        : Number(formData.monthlySonnetCredits),
    monthlyOpusCredits:
      formData.monthlyOpusCredits === null
        ? null
        : Number(formData.monthlyOpusCredits),
    // [B1 FIX] Pass through legacy fields to prevent backend wiping them to NULL
    allowedModelFamilies: formData.allowedModelFamilies,
    limitMessagesPerMonth:
      formData.limitMessagesPerMonth === null
        ? null
        : Number(formData.limitMessagesPerMonth),
    features,
  };

  if (!isEditing) {
    payload.id = formData.id.trim();
  }

  return payload;
}
